import { Portal } from '@radix-ui/react-portal'
import clsx from 'clsx'
import { ChevronsUpIcon } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { useEffect, useId, useState } from 'react'

import { AnimatedCurrency } from 'components/member_onboarding_v2/components/AnimatedCurrency'
import { SkeletonItem } from 'components/reusable/SkeletonItem'

import { useProspect } from '../setup/ProspectProvider'
import { SupportLink } from '../SupportLink'
import { QuoteCostDetails } from './QuoteCostDetails'
import { QuoteReviewButton } from './QuoteReviewButton'
import { useSelfServeExplorerContext } from './QuoteSelfServeExplorerContext'

export function QuoteCostBottomSheet () {
  const [isOpen, setIsOpen] = useState(false)
  const id = useId()

  const { totalEmployerCost, isLoading } = useSelfServeExplorerContext()
  const { prospect } = useProspect()
  const discount = prospect?.discount

  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'

      return () => {
        document.body.style.overflow = originalOverflow
      }
    }
  }, [isOpen])

  return (
    <Portal>
      <div
        id={id}
        className={clsx('fixed inset-0 transition duration-300', {
          'pointer-events-none bg-black/0': !isOpen,
          'bg-black/40': isOpen,
        })}
        onClick={() => {
          setIsOpen(false)
        }}
      />
      <div className='fixed bottom-0 left-0 right-0'>
        <div className='p-4 md:p-6 bg-white rounded-t-2xl border-emphasis shadow-emphasis'>
          <div className='flex items-center justify-between gap-4'>
            <div className='flex flex-col h-full'>
              <h2 className='caption'>Estimated total monthly cost</h2>
              <div className='heading-lg text-gray-800'>
                {isLoading
                  ? (
                    <SkeletonItem widthClass='w-32' heightClass='h-8' />
                    )
                  : (
                    <AnimatedCurrency value={totalEmployerCost} />
                    )}
              </div>
            </div>
            <QuoteReviewButton width='auto' />
          </div>
          <hr className='mt-2 mb-4' />
          <AnimatePresence>
            {isOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0, overflow: 'hidden' }}
                animate={{ opacity: 1, height: 'auto', overflow: 'auto' }}
                exit={{ opacity: 0, height: 0, overflow: 'hidden' }}
                transition={{ duration: 0.3 }}
              >
                <div className='mb-4'>
                  <QuoteCostDetails discount={discount} />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          <div className='flex items-center gap-4 justify-between'>
            {!isOpen
              ? (
                <button
                  className='appearance-none text-thatch-500 flex items-center gap-2 body-bold'
                  onClick={() => {
                    setIsOpen(true)
                  }}
                >
                  <ChevronsUpIcon size={16} />
                  <span>See cost breakdown</span>
                </button>
                )
              : (
                <div />
                )}
            <SupportLink />
          </div>
        </div>
      </div>
    </Portal>
  )
}
